import { ConverterService, CryptoProvider, SavePinService } from '../../../providers';
import { Injectable, Signal, inject } from '@angular/core';
import { UserService } from '../user.service';
import { UserServiceInterface } from '../interface';

@Injectable({
  providedIn: 'root',
})
export class WebUserServiceMigrationDecorator implements UserServiceInterface {
  private readonly converterService = inject(ConverterService);
  private readonly cryptoService = inject(CryptoProvider);
  private readonly userService = inject(UserService);
  private readonly savePinService = inject(SavePinService);

  public hasSecondFactor: Signal<boolean | undefined> = this.userService.hasSecondFactor;
  public userSecret: Signal<string | undefined> = this.userService.userSecret;
  public username: Signal<string | undefined> = this.userService.username;

  public async getSecondFactor(): Promise<string | null> {
    await this.migrateOldSecondFactor();
    const encryptedPin = await this.savePinService.getPin(this.getBase64Username());
    if (!encryptedPin) {
      return null;
    }
    return await this.decryptWithUserSecret(encryptedPin);
  }

  public async resetSecondFactor(): Promise<void> {
    await this.savePinService.resetPin(this.getBase64Username());
    return Promise.resolve();
  }

  public async setSecondFactor(secondFactor: string): Promise<void> {
    if (secondFactor === '') {
      throw new Error('Second factor cannot be empty');
    }

    const value = await this.encryptWithUserSecret(secondFactor);
    if (value !== null) {
      await this.savePinService.setPin(this.getBase64Username(), value);
    }
  }

  private async decryptWithUserSecret(encryptedPin: string): Promise<string | null> {
    try {
      const userSecret = this.getUserSecretOrThrowError();
      const data = await this.cryptoService.decrypt(userSecret, encryptedPin, Uint16Array);
      return this.converterService.uIntArrayToString(data);
    } catch (e) {
      return null;
    }
  }

  private async encryptWithUserSecret(stringToEncrypt: string): Promise<string | null> {
    try {
      const dataToEncrypt: Uint16Array = this.converterService.stringToUint16Array(stringToEncrypt);

      const userSecret = this.getUserSecretOrThrowError();
      return this.cryptoService.encryptToHex(userSecret, dataToEncrypt);
    } catch (e) {
      return null;
    }
  }

  private getBase64Username(): string {
    try {
      const username = this.getUsernameOrThrowError();
      return window.btoa(username);
    } catch (e) {
      return '';
    }
  }

  private async migrateOldSecondFactor(): Promise<void> {
    const oldSecondFactorKey = 'pin';
    const encryptedSecondFactor = localStorage.getItem(oldSecondFactorKey);
    if (!encryptedSecondFactor) {
      return;
    }
    if (encryptedSecondFactor) {
      const decryptedSecondFactor = await this.decryptWithUserSecret(encryptedSecondFactor);
      if (decryptedSecondFactor !== null) {
        await this.setSecondFactor(decryptedSecondFactor);
      }
      await this.savePinService.resetPin(oldSecondFactorKey);
    }
  }

  private getUsernameOrThrowError(): string {
    const username = this.username();
    if (!username) {
      throw new Error('Username is undefined');
    }
    return username;
  }

  private getUserSecretOrThrowError(): string {
    const userSecret = this.userSecret();
    if (!userSecret) {
      throw new Error('User secret is undefined');
    }
    return userSecret;
  }
}
